import axios from 'axios';

// Railway backend URL for direct API calls (from env or fallback)
const RAILWAY_BACKEND_URL = process.env.REACT_APP_RAILWAY_BACKEND_URL || process.env.REACT_APP_BACKEND_URL || 'https://revstar-main-platform-production.up.railway.app';

// Platform domain - used to detect platform subdomains vs custom domains
const PLATFORM_DOMAIN = process.env.REACT_APP_PLATFORM_DOMAIN || 'userevstar.com';

/**
 * Determine the correct API base URL based on the current environment
 *
 * The /api proxy works on:
 * - Localhost (CRA dev server proxy)
 * - Vercel deployments (vercel.json rewrites)
 * - Platform subdomains (e.g., dashboard.staging.userevstar.com) - hosted on Vercel with custom domains
 *
 * Custom domains (merchant storefronts) served by CloudFront don't have /api proxy,
 * so they need to call Railway backend directly.
 */
const getApiBaseUrl = () => {
  // If explicitly set via env var, use it
  const rawBase = process.env.REACT_APP_BACKEND_URL;
  if (rawBase && rawBase.trim() !== '') {
    const url = rawBase.endsWith('/api') ? rawBase : `${rawBase}/api`;
    console.log('[axios] Using REACT_APP_BACKEND_URL:', url);
    return url;
  }

  // Check current hostname to determine routing
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;

    // Localhost - use proxy (CRA dev server handles it)
    // Also handle *.localhost subdomains for local development
    if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.localhost')) {
      console.log('[axios] Localhost detected, using /api proxy');
      return '/api';
    }

    // Vercel deployment - use proxy (Vercel rewrites handle it)
    if (hostname.endsWith('.vercel.app')) {
      console.log('[axios] Vercel deployment detected, using /api proxy');
      return '/api';
    }

    // Platform subdomains (e.g., dashboard.staging.userevstar.com, my.userevstar.com)
    // These are hosted on Vercel with custom domains and have /api rewrites configured
    if (hostname.endsWith(PLATFORM_DOMAIN)) {
      console.log('[axios] Platform subdomain detected:', hostname);
      console.log('[axios] Using /api proxy (Vercel custom domain with rewrites)');
      return '/api';
    }

    // All other domains (merchant custom domains served by CloudFront, etc.)
    // These don't have /api proxy capability, call Railway directly
    console.log('[axios] Custom/CloudFront domain detected:', hostname);
    console.log('[axios] Using Railway backend directly:', RAILWAY_BACKEND_URL);
    return `${RAILWAY_BACKEND_URL}/api`;
  }

  // Fallback to proxy
  return '/api';
};

const baseURL = getApiBaseUrl();

// Create a configured axios instance for Redux slices
const axiosInstance = axios.create({
  baseURL,
  withCredentials: true, // Always include cookies for authentication
  timeout: 120000, // 2 minutes timeout for long-running operations like campaign sending
});

// Request interceptor - add cart ID to every request
axiosInstance.interceptors.request.use(
  (config) => {
    // HTTP-only cookies are automatically included with withCredentials: true

    // INDUSTRY STANDARD: Send cart ID with every request for persistence across sessions
    const cartId = localStorage.getItem('revstar_cart_id');
    if (cartId) {
      config.headers['X-Cart-ID'] = cartId;
      console.log('[axios] Sending cart ID in request:', cartId);
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for handling auth errors and capturing cart ID
axiosInstance.interceptors.response.use(
  (response) => {
    // INDUSTRY STANDARD: Capture and store cart ID from response headers
    const cartId = response.headers['x-cart-id'];
    if (cartId) {
      const existingCartId = localStorage.getItem('revstar_cart_id');
      if (existingCartId !== cartId) {
        console.log('[axios] Received NEW cart ID from server:', cartId);
        localStorage.setItem('revstar_cart_id', cartId);
      }
    }

    // Also check response body for cart ID (fallback)
    if (response.data?.cart?.cartId) {
      const cartIdFromBody = response.data.cart.cartId;
      const existingCartId = localStorage.getItem('revstar_cart_id');
      if (existingCartId !== cartIdFromBody) {
        console.log('[axios] Received cart ID from response body:', cartIdFromBody);
        localStorage.setItem('revstar_cart_id', cartIdFromBody);
      }
    }

    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Public routes that should NOT redirect to login on 401
      const publicRoutes = [
        '/payment/verify',
        '/order-confirmation',
        '/team/accept',
        // Storefront routes - guests should be able to browse without login
        '/product/',      // Product pages (including appointments)
        '/shop',          // Shop page
        '/category/',     // Category pages
        '/cart',          // Shopping cart
        '/checkout',      // Checkout (has its own auth handling)
        '/about',         // About page
        '/contact',       // Contact page
        '/advertise',     // Advertise/pricing page
        '/s/',            // Store slug routes (/s/:slug/*)
      ];
      const isPublicRoute = publicRoutes.some(route => window.location.pathname.includes(route));

      // Check if on a subdomain storefront (e.g., gthub-stores.localhost:3000)
      const hostname = window.location.hostname;
      const isSubdomainStorefront = hostname.includes('.') &&
                                     !hostname.startsWith('www.') &&
                                     hostname !== 'localhost';

      // Only redirect to login if:
      // 1. Not on a public route
      // 2. Not on a subdomain storefront
      // 3. Not already on login page
      if (!isPublicRoute && !isSubdomainStorefront && window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export default axiosInstance;
